import { createId } from '../core/project';
import type { Asset } from '../core/schema';
import { getAssetBlob, getDerivedBlob, putAssetBlob, putDerivedBlob } from './persistence';
import { generateWaveform } from './waveformService';

class MediaRegistry {
  private urls = new Map<string, string>();
  private elements = new Map<string, HTMLVideoElement | HTMLImageElement>();
  private thumbnails = new Map<string, string>();

  private key(projectId: string, assetId: string) { return `${projectId}:${assetId}`; }

  register(projectId: string, assetId: string, blob: Blob) {
    const key = this.key(projectId, assetId);
    const old = this.urls.get(key);
    if (old) URL.revokeObjectURL(old);
    const url = URL.createObjectURL(blob);
    this.urls.set(key, url);
    this.elements.delete(key);
    return url;
  }

  async url(projectId: string, assetId: string): Promise<string | null> {
    const key = this.key(projectId, assetId);
    if (this.urls.has(key)) return this.urls.get(key)!;
    const blob = await getAssetBlob(projectId, assetId);
    return blob ? this.register(projectId, assetId, blob) : null;
  }

  async element(projectId: string, asset: Asset): Promise<HTMLVideoElement | HTMLImageElement | null> {
    const key = this.key(projectId, asset.id);
    if (this.elements.has(key)) return this.elements.get(key)!;
    const url = await this.url(projectId, asset.id);
    if (!url) return null;
    if (asset.kind === 'video') {
      const video = document.createElement('video');
      video.src = url;
      video.muted = true;
      video.playsInline = true;
      video.preload = 'auto';
      await new Promise<void>((resolve) => {
        video.onloadeddata = () => resolve();
        video.onerror = () => resolve();
        setTimeout(resolve, 3000);
      });
      this.elements.set(key, video);
      return video;
    }
    if (asset.kind === 'image') {
      const image = new Image();
      image.src = url;
      await image.decode().catch(() => undefined);
      this.elements.set(key, image);
      return image;
    }
    return null;
  }

  async thumbnailUrl(projectId: string, assetId: string): Promise<string | null> {
    const key = this.key(projectId, assetId);
    if (this.thumbnails.has(key)) return this.thumbnails.get(key)!;
    const blob = await getDerivedBlob(projectId, assetId, 'thumbnail');
    if (!blob) return null;
    const url = URL.createObjectURL(blob);
    this.thumbnails.set(key, url);
    return url;
  }

  clearProject(projectId: string) {
    for (const [key, url] of [...this.urls, ...this.thumbnails]) if (key.startsWith(`${projectId}:`)) URL.revokeObjectURL(url);
    for (const key of this.urls.keys()) if (key.startsWith(`${projectId}:`)) this.urls.delete(key);
    for (const key of this.thumbnails.keys()) if (key.startsWith(`${projectId}:`)) this.thumbnails.delete(key);
    for (const key of this.elements.keys()) if (key.startsWith(`${projectId}:`)) this.elements.delete(key);
  }
}

export const mediaRegistry = new MediaRegistry();

function assetKind(file: File): Asset['kind'] {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type.startsWith('audio/')) return 'audio';
  if (file.type.startsWith('image/')) return 'image';
  if (/\.(glb|gltf|obj|fbx)$/i.test(file.name)) return 'model';
  if (file.type.startsWith('font/')) return 'font';
  return 'other';
}

async function hashFile(file: File): Promise<string> {
  const chunk = file.size > 4_000_000 ? file.slice(0, 4_000_000) : file;
  const buffer = await chunk.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((value) => value.toString(16).padStart(2, '0')).join('');
}

async function mediaMetadata(file: File, kind: Asset['kind'], url: string): Promise<Partial<Asset>> {
  if (kind === 'video' || kind === 'audio') {
    const media = document.createElement(kind === 'video' ? 'video' : 'audio');
    media.preload = 'metadata';
    media.src = url;
    await new Promise<void>((resolve) => {
      media.onloadedmetadata = () => resolve();
      media.onerror = () => resolve();
      setTimeout(resolve, 5000);
    });
    return {
      duration: Number.isFinite(media.duration) ? media.duration : undefined,
      width: kind === 'video' ? (media as HTMLVideoElement).videoWidth || undefined : undefined,
      height: kind === 'video' ? (media as HTMLVideoElement).videoHeight || undefined : undefined
    };
  }
  if (kind === 'image') {
    const image = new Image();
    image.src = url;
    await image.decode().catch(() => undefined);
    return { width: image.naturalWidth || undefined, height: image.naturalHeight || undefined };
  }
  return {};
}

async function generateThumbnail(file: File, kind: Asset['kind'], url: string): Promise<Blob | null> {
  if (kind !== 'video' && kind !== 'image') return null;
  let source: CanvasImageSource;
  if (kind === 'image') {
    const image = new Image();
    image.src = url;
    await image.decode();
    source = image;
  } else {
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'auto';
    video.src = url;
    await new Promise<void>((resolve, reject) => { video.onloadedmetadata = () => resolve(); video.onerror = () => reject(video.error); });
    video.currentTime = Math.min(1, Math.max(0, video.duration * 0.1));
    await new Promise<void>((resolve) => { video.onseeked = () => resolve(); setTimeout(resolve, 1000); });
    source = video;
  }
  const canvas = document.createElement('canvas');
  canvas.width = 320;
  canvas.height = 180;
  const context = canvas.getContext('2d')!;
  const width = 'videoWidth' in source ? source.videoWidth : (source as HTMLImageElement).naturalWidth;
  const height = 'videoHeight' in source ? source.videoHeight : (source as HTMLImageElement).naturalHeight;
  const scale = Math.max(canvas.width / width, canvas.height / height);
  context.drawImage(source, (canvas.width - width * scale) / 2, (canvas.height - height * scale) / 2, width * scale, height * scale);
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.78));
}

export async function ingestFile(projectId: string, file: File): Promise<Asset> {
  const kind = assetKind(file);
  const id = createId('asset');
  const url = mediaRegistry.register(projectId, id, file);
  const [metadata, hash] = await Promise.all([mediaMetadata(file, kind, url), hashFile(file).catch(() => undefined)]);
  const asset: Asset = {
    id, name: file.name, kind, mimeType: file.type || 'application/octet-stream', size: file.size,
    hash, missing: false, proxyStatus: 'none', cacheStatus: 'local', createdAt: Date.now(), ...metadata
  };
  await putAssetBlob(projectId, id, file);
  generateThumbnail(file, kind, url)
    .then((thumbnail) => thumbnail && putDerivedBlob(projectId, id, 'thumbnail', thumbnail))
    .catch(() => undefined);
  if (kind === 'audio' || kind === 'video') void generateWaveform(projectId, id, file).catch(() => undefined);
  return asset;
}
